import { formatClinicDateOnly } from '@/lib/questionnaireDateFormat';

/** Answer shapes the Clinio Forms builder stores. */
export type FormsAnswerQuestion = {
  id?: string;
  type: string;
  options?: Array<{
    value: string;
    label: string;
    label_da?: string;
    score?: number;
  }>;
  scaleMin?: number;
  scaleMax?: number;
  scaleLabels?: string[];
};

export type TopicRowAnswer = {
  comment: string;
  scale: string | null;
};

const UNSCORED_TYPES = new Set(['text', 'date', 'information']);

const TOPIC_ANSWER_KEYS = ['comment', 'note', 'scale', 'level'] as const;

function topicText(record: Record<string, unknown>, keys: readonly string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string') return value;
  }
  return '';
}

function topicLevel(record: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) continue;
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return null;
}

/** Level + note, stored as `{ comment, scale }` or `{ level, note }`. */
export function topicRowParts(response: unknown): TopicRowAnswer | null {
  if (typeof response !== 'object' || response === null || Array.isArray(response)) return null;
  const record = response as Record<string, unknown>;
  if (!TOPIC_ANSWER_KEYS.some((key) => Object.prototype.hasOwnProperty.call(record, key)))
    return null;
  return {
    comment: topicText(record, ['comment', 'note']),
    scale: topicLevel(record, ['scale', 'level']),
  };
}

function optionLabel(
  option: { label: string; label_da?: string } | undefined,
  fallback: string,
): string {
  const label = option?.label_da?.trim() || option?.label?.trim();
  return label || fallback;
}

function yesNo(value: boolean, language: string): string {
  const english = language.toLowerCase().startsWith('en');
  if (value) return english ? 'Yes' : 'Ja';
  return english ? 'No' : 'Nej';
}

function formatLoose(response: unknown, language: string): string {
  if (response == null || response === '') return '—';
  const topic = topicRowParts(response);
  if (topic) return formatTopic({ type: 'topic_row' }, topic);
  if (Array.isArray(response)) {
    const parts = response
      .map((item) => formatLoose(item, language))
      .filter((part) => part !== '—');
    return parts.length > 0 ? parts.join(', ') : '—';
  }
  if (typeof response === 'string') return response.trim() || '—';
  if (typeof response === 'number' && Number.isFinite(response)) return String(response);
  if (typeof response === 'boolean') return yesNo(response, language);
  if (typeof response === 'object') {
    const parts = Object.values(response as Record<string, unknown>)
      .filter((value) => value != null && value !== '' && typeof value !== 'object')
      .map((value) => (typeof value === 'boolean' ? yesNo(value, language) : String(value).trim()))
      .filter((value) => value.length > 0);
    return parts.length > 0 ? parts.join(', ') : '—';
  }
  return '—';
}

function formatTopic(question: FormsAnswerQuestion, topic: TopicRowAnswer): string {
  const lines: string[] = [];
  if (topic.scale) {
    const option = question.options?.find((item) => item.value === topic.scale);
    lines.push(optionLabel(option, topic.scale));
  }
  const comment = topic.comment.trim();
  if (comment) lines.push(comment);
  return lines.length > 0 ? lines.join('\n') : '—';
}

function formatScale(question: FormsAnswerQuestion, response: unknown, language: string): string {
  const numeric =
    typeof response === 'number'
      ? response
      : typeof response === 'string'
        ? Number(response)
        : Number.NaN;
  if (!Number.isFinite(numeric)) return formatLoose(response, language);
  const max = question.scaleMax ?? 10;
  const min = question.scaleMin ?? 0;
  const label = question.scaleLabels?.[numeric - min]?.trim();
  const fraction = `${numeric} / ${max}`;
  return label ? `${label} (${fraction})` : fraction;
}

export function formatResponse(
  question: FormsAnswerQuestion,
  response: unknown,
  language: string,
): string {
  if (response == null || response === '') return '—';

  const topic = topicRowParts(response);
  if (question.type === 'topic_row') {
    if (topic) return formatTopic(question, topic);
    if (typeof response === 'string' && response.trim()) {
      const option = question.options?.find((item) => item.value === response);
      return optionLabel(option, response.trim());
    }
    return formatLoose(response, language);
  }

  if (
    topic &&
    question.type !== 'multiple_choice' &&
    question.type !== 'checkbox' &&
    question.type !== 'scale'
  ) {
    return formatTopic(question, topic);
  }

  switch (question.type) {
    case 'multiple_choice': {
      const option = question.options?.find((item) => item.value === response);
      return option
        ? optionLabel(option, '') || formatLoose(response, language)
        : formatLoose(response, language);
    }
    case 'checkbox': {
      const values = Array.isArray(response) ? response : [response];
      const labels = values
        .map((value) => {
          const option = question.options?.find((item) => item.value === value);
          return option
            ? optionLabel(option, '') || formatLoose(value, language)
            : formatLoose(value, language);
        })
        .filter((label) => label !== '—');
      return labels.length > 0 ? labels.join(', ') : '—';
    }
    case 'scale':
      return formatScale(question, response, language);
    case 'date':
      return formatClinicDateOnly(String(response), language) || String(response);
    case 'text':
      return typeof response === 'string'
        ? response.trim() || '—'
        : formatLoose(response, language);
    case 'information':
      return '—';
    default:
      return formatLoose(response, language);
  }
}

export function answerPoints(question: FormsAnswerQuestion, response: unknown): number | null {
  if (UNSCORED_TYPES.has(question.type)) return null;

  const topic = topicRowParts(response);
  if (question.type === 'topic_row' || topic) {
    const scale =
      topic?.scale ??
      (question.type === 'topic_row' && typeof response === 'string' ? response : null);
    if (!scale) return null;
    const option = question.options?.find((item) => item.value === scale);
    return typeof option?.score === 'number' ? option.score : null;
  }

  if (question.type === 'checkbox' && Array.isArray(response)) {
    let sum = 0;
    let any = false;
    for (const value of response) {
      const option = question.options?.find((item) => item.value === value);
      if (typeof option?.score === 'number') {
        sum += option.score;
        any = true;
      }
    }
    return any ? sum : null;
  }

  if (question.type === 'scale') {
    if (typeof response === 'number' && Number.isFinite(response)) return response;
    if (typeof response === 'string' && response.trim() !== '') {
      const parsed = Number(response);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  if (typeof response === 'number' && Number.isFinite(response)) return response;
  const option = question.options?.find((item) => item.value === response);
  if (option && typeof option.score === 'number') return option.score;
  if (typeof response === 'string' && /^\d+$/.test(response)) return Number(response);
  return null;
}

export function definedPointTotal(
  questions: ReadonlyArray<FormsAnswerQuestion & { id: string }>,
  responses: Record<string, unknown>,
): number | null {
  let sum = 0;
  let any = false;
  for (const question of questions) {
    const points = answerPoints(question, responses[question.id]);
    if (points == null) continue;
    sum += points;
    any = true;
  }
  return any ? sum : null;
}

/**
 * Header total to show instead of a stored 0.
 * Returns null when the stored score (including an SF-36 physical/mental pair
 * or a real 0 on a domain-scored form) should be left as it is.
 */
export function replacementTotalScore(input: {
  kind: 'none' | 'total' | 'physical_mental';
  storedValue: number | null;
  pointTotal: number | null;
  domainTotal: number | null;
  hasDomainScores: boolean;
}): number | null {
  if (input.kind === 'physical_mental') return null;
  if (input.hasDomainScores) {
    if (
      input.domainTotal != null &&
      input.domainTotal > 0 &&
      (input.kind !== 'total' || input.storedValue === 0)
    ) {
      return input.domainTotal;
    }
    return null;
  }
  if (input.kind === 'total' && input.storedValue !== 0) return null;
  if (input.pointTotal != null && input.pointTotal > 0) return input.pointTotal;
  return null;
}
